import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlusIcon as UserPlus, UserGearIcon as UserGear, TrashIcon as Trash, ShieldCheckIcon as ShieldCheck, PencilSimpleIcon as PencilSimple, KeyIcon as Key, ShieldWarningIcon as ShieldWarning, ProhibitIcon as Prohibit, CheckCircleIcon as CheckCircle, SignInIcon as SignIn, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, MagnifyingGlassIcon as MagnifyingGlass } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Amministratore", team: "Tecnico", client: "Cliente" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [force2fa, setForce2fa] = useState(false);
  const [clients, setClients] = useState([]);
  const [openClient, setOpenClient] = useState(false);
  const [openStaff, setOpenStaff] = useState(false);
  const [cForm, setCForm] = useState({ email: "", password: "", name: "", cliente_id: "", sso_enabled: false });
  const [sForm, setSForm] = useState({ email: "", password: "", name: "", role: "team", sso_enabled: false });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [pwdUser, setPwdUser] = useState(null);
  const [pwdVal, setPwdVal] = useState("");

  const load = () => api.get("/users").then(({ data }) => { setUsers(data.users || []); setForce2fa(!!data.force_2fa_clients); });
  useEffect(() => { load(); api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.ragione_sociale || "—";
  const staff = users.filter((u) => u.role === "admin" || u.role === "team");
  const clientUsers = users.filter((u) => u.role === "client");

  const [staffQ, setStaffQ] = useState("");
  const [clientQ, setClientQ] = useState("");
  const [staffSort, setStaffSort] = useState({ key: "name", dir: "asc" });
  const [clientSort, setClientSort] = useState({ key: "name", dir: "asc" });

  const sortVal = (u, key) => {
    switch (key) {
      case "name": return (u.name || "").toLowerCase();
      case "email": return (u.email || "").toLowerCase();
      case "role": return ROLE_LABEL[u.role] || "";
      case "cliente": return clientName(u.client_id).toLowerCase();
      case "stato": return u.disabled ? 1 : 0;
      case "twofa": return u.totp_enabled ? 1 : 0;
      case "sso": return u.sso_enabled ? 1 : 0;
      default: return "";
    }
  };
  const processUsers = (list, q, sort) => {
    const term = q.trim().toLowerCase();
    let out = term
      ? list.filter((u) => (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term) || clientName(u.client_id).toLowerCase().includes(term))
      : list;
    out = [...out].sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  };
  const toggleSort = (setSort, key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const SortHead = ({ label, k, sort, setSort, className }) => (
    <TableHead className={`cursor-pointer select-none hover:text-primary ${className || ""}`} onClick={() => toggleSort(setSort, k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1">{label}{sort.key === k && (sort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}</span>
    </TableHead>
  );
  const staffRows = processUsers(staff, staffQ, staffSort);
  const clientRows = processUsers(clientUsers, clientQ, clientSort);

  const saveClient = async () => {
    if (!cForm.email || !cForm.password || !cForm.cliente_id) { toast.error("Compila email, password e cliente"); return; }
    try { await api.post("/users", cForm); toast.success("Accesso cliente creato"); setOpenClient(false); setCForm({ email: "", password: "", name: "", cliente_id: "", sso_enabled: false }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveStaff = async () => {
    if (!sForm.email || !sForm.password) { toast.error("Compila email e password"); return; }
    try { await api.post("/staff", sForm); toast.success("Utente interno creato"); setOpenStaff(false); setSForm({ email: "", password: "", name: "", role: "team", sso_enabled: false }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delUser = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSso = async (u, val) => {
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, sso_enabled: val } : x)));
    try { await api.patch(`/users/${u.id}`, { sso_enabled: val }); toast.success(val ? "Accesso SSO abilitato" : "Accesso SSO disabilitato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  const toggleActive = async (u) => {
    const disabled = !u.disabled;
    try {
      await api.patch(`/users/${u.id}`, { disabled });
      toast.success(disabled ? "Account disabilitato" : "Account abilitato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (u) => { setEditUser(u); setEditForm({ name: u.name || "", email: u.email || "" }); };
  const saveEdit = async () => {
    const body = { name: editForm.name };
    if (editUser.role === "client" && editForm.email !== editUser.email) body.email = editForm.email;
    try { await api.patch(`/users/${editUser.id}`, body); toast.success("Utente aggiornato"); setEditUser(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doResetPassword = async () => {
    if ((pwdVal || "").length < 8) { toast.error("Minimo 8 caratteri"); return; }
    try { await api.post(`/users/${pwdUser.id}/reset-password`, { new_password: pwdVal }); toast.success("Password reimpostata"); setPwdUser(null); setPwdVal(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Azzerare la 2FA di ${u.email}? L'utente dovrà riconfigurarla.`)) return;
    try { await api.post(`/users/${u.id}/2fa/reset`); toast.success("2FA azzerata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleForce2fa = async (val) => {
    setForce2fa(val);
    try { await api.put("/users/config/force-2fa-clients", { enabled: val }); toast.success(val ? "2FA obbligatoria per i clienti" : "2FA non più obbligatoria"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setForce2fa(!val); }
  };

  const StatusCell = ({ u }) => (
    <div className="flex items-center gap-2">
      <Badge className={`rounded-sm ${u.disabled ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}>{u.disabled ? "Disabilitato" : "Attivo"}</Badge>
      <Switch checked={!u.disabled} onCheckedChange={() => toggleActive(u)} data-testid={`active-switch-${u.id}`} />
    </div>
  );
  const TwoFaCell = ({ u }) => (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className={`rounded-sm ${u.totp_enabled ? "text-emerald-700 border-emerald-200" : "text-muted-foreground"}`}>{u.totp_enabled ? "Attiva" : "—"}</Badge>
      {u.totp_enabled && <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" title="Azzera 2FA" onClick={() => reset2fa(u)} data-testid={`reset-2fa-${u.id}`}><ShieldWarning size={15} /></Button>}
    </div>
  );
  const impersonate = async (u) => {
    if (!window.confirm(`Aprire l'area cliente come ${u.email}? Verrai disconnesso dal pannello admin fino al rientro.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/impersonate`);
      const admin = localStorage.getItem("token");
      if (admin) localStorage.setItem("impersonator_token", admin);
      localStorage.setItem("token", data.token);
      window.location.href = "/portale";
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const RowActions = ({ u }) => (
    <div className="flex items-center justify-end gap-1">
      {u.role === "client" && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Impersona (vedi area cliente)" onClick={() => impersonate(u)} data-testid={`impersonate-user-${u.id}`}><SignIn size={16} /></Button>}
      {u.role === "client" && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-amber-600" title="Reset 2FA cliente" onClick={() => reset2fa(u)} data-testid={`row-reset-2fa-${u.id}`}><ShieldWarning size={16} /></Button>}
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Modifica" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" title="Reset password" onClick={() => { setPwdUser(u); setPwdVal(""); }} data-testid={`pwd-user-${u.id}`}><Key size={16} /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Elimina" onClick={() => delUser(u)} data-testid={`del-user-${u.id}`}><Trash size={16} /></Button>
    </div>
  );

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Utenti & Tecnici" subtitle="Gestione accessi interni e portale clienti" />

        <Tabs defaultValue="staff">
          <TabsList className="rounded-sm mb-4">
            <TabsTrigger value="staff" data-testid="tab-staff">Amministratori & Tecnici</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">Clienti</TabsTrigger>
          </TabsList>

          <TabsContent value="staff">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="relative w-full sm:w-80">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={staffQ} onChange={(e) => setStaffQ(e.target.value)} placeholder="Cerca per nome o email…" className="pl-8 rounded-sm bg-white" data-testid="staff-search" />
              </div>
              <Button data-testid="new-staff-btn" onClick={() => setOpenStaff(true)} className="rounded-sm gap-2"><UserGear size={16} weight="bold" /> Nuovo utente interno</Button>
            </div>
            <Card className="rounded-sm border-border shadow-none">
              <Table>
                <TableHeader><TableRow>
                  <SortHead label="Nome" k="name" sort={staffSort} setSort={setStaffSort} />
                  <SortHead label="Email" k="email" sort={staffSort} setSort={setStaffSort} />
                  <SortHead label="Ruolo" k="role" sort={staffSort} setSort={setStaffSort} />
                  <SortHead label="Stato" k="stato" sort={staffSort} setSort={setStaffSort} />
                  <SortHead label="2FA" k="twofa" sort={staffSort} setSort={setStaffSort} />
                  <SortHead label="SSO" k="sso" sort={staffSort} setSort={setStaffSort} />
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {staffRows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun utente trovato.</TableCell></TableRow>}
                  {staffRows.map((u) => (
                    <TableRow key={u.id} data-testid="staff-row" className="hover:bg-secondary">
                      <TableCell className="font-medium">{u.name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-sm">{ROLE_LABEL[u.role]}</Badge></TableCell>
                      <TableCell><StatusCell u={u} /></TableCell>
                      <TableCell><TwoFaCell u={u} /></TableCell>
                      <TableCell><Switch checked={!!u.sso_enabled} onCheckedChange={(v) => toggleSso(u, v)} data-testid="staff-sso-switch" /></TableCell>
                      <TableCell className="text-right"><RowActions u={u} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="clients">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative w-full sm:w-72">
                  <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Cerca per nome, email o cliente…" className="pl-8 rounded-sm bg-white" data-testid="client-search" />
                </div>
                <label className="flex items-center gap-2 text-sm border border-border rounded-sm px-3 py-2 bg-secondary/40" data-testid="force-2fa-toggle-wrap">
                  <Switch checked={force2fa} onCheckedChange={toggleForce2fa} data-testid="force-2fa-toggle" />
                  <ShieldCheck size={15} className="text-primary" /> 2FA obbligatoria per i clienti
                </label>
              </div>
              <Button data-testid="new-user-btn" onClick={() => setOpenClient(true)} className="rounded-sm gap-2"><UserPlus size={16} weight="bold" /> Nuovo accesso cliente</Button>
            </div>
            <Card className="rounded-sm border-border shadow-none">
              <Table>
                <TableHeader><TableRow>
                  <SortHead label="Nome" k="name" sort={clientSort} setSort={setClientSort} />
                  <SortHead label="Email" k="email" sort={clientSort} setSort={setClientSort} />
                  <SortHead label="Cliente collegato" k="cliente" sort={clientSort} setSort={setClientSort} />
                  <SortHead label="Stato" k="stato" sort={clientSort} setSort={setClientSort} />
                  <SortHead label="2FA" k="twofa" sort={clientSort} setSort={setClientSort} />
                  <SortHead label="SSO" k="sso" sort={clientSort} setSort={setClientSort} />
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {clientRows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">{clientQ ? "Nessun accesso cliente trovato." : "Nessun accesso cliente."}</TableCell></TableRow>}
                  {clientRows.map((u) => (
                    <TableRow key={u.id} data-testid="client-user-row" className="hover:bg-secondary">
                      <TableCell className="font-medium">{u.name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell className="text-muted-foreground">{clientName(u.client_id)}</TableCell>
                      <TableCell><StatusCell u={u} /></TableCell>
                      <TableCell><TwoFaCell u={u} /></TableCell>
                      <TableCell><Switch checked={!!u.sso_enabled} onCheckedChange={(v) => toggleSso(u, v)} data-testid="client-sso-switch" /></TableCell>
                      <TableCell className="text-right"><RowActions u={u} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={openStaff} onOpenChange={setOpenStaff}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nuovo utente interno</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Nome</Label><Input value={sForm.name} onChange={(e) => setSForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Email *</Label><Input type="email" data-testid="staff-email" value={sForm.email} onChange={(e) => setSForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Password *</Label><Input type="text" data-testid="staff-password" value={sForm.password} onChange={(e) => setSForm((f) => ({ ...f, password: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Ruolo</Label>
              <Select value={sForm.role} onValueChange={(v) => setSForm((f) => ({ ...f, role: v }))}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="team">Tecnico</SelectItem><SelectItem value="admin">Amministratore</SelectItem></SelectContent></Select></div>
            <div className="flex items-center gap-3 pt-1"><Switch checked={sForm.sso_enabled} onCheckedChange={(v) => setSForm((f) => ({ ...f, sso_enabled: v }))} data-testid="staff-sso-toggle" /><Label className="text-sm flex items-center gap-1.5"><ShieldCheck size={15} className="text-primary" /> Abilita accesso SSO Microsoft</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenStaff(false)} className="rounded-sm">Annulla</Button><Button data-testid="save-staff-btn" onClick={saveStaff} className="rounded-sm">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openClient} onOpenChange={setOpenClient}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nuovo accesso cliente</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Cliente *</Label>
              <div className="mt-1"><ClientCombobox clients={clients} value={cForm.cliente_id} onChange={(v) => setCForm((f) => ({ ...f, cliente_id: v }))} placeholder="Seleziona" testid="user-cliente" /></div></div>
            <div><Label className="text-xs uppercase tracking-wider">Nome referente</Label><Input value={cForm.name} onChange={(e) => setCForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Email *</Label><Input type="email" data-testid="user-email" value={cForm.email} onChange={(e) => setCForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Password *</Label><Input type="text" data-testid="user-password" value={cForm.password} onChange={(e) => setCForm((f) => ({ ...f, password: e.target.value }))} className="mt-1 rounded-sm" /></div>
            <div className="flex items-center gap-3 pt-1"><Switch checked={cForm.sso_enabled} onCheckedChange={(v) => setCForm((f) => ({ ...f, sso_enabled: v }))} data-testid="client-sso-toggle" /><Label className="text-sm flex items-center gap-1.5"><ShieldCheck size={15} className="text-primary" /> Abilita accesso SSO Microsoft</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpenClient(false)} className="rounded-sm">Annulla</Button><Button data-testid="save-user-btn" onClick={saveClient} className="rounded-sm">Crea accesso</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modifica utente */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-heading">Modifica {editUser ? ROLE_LABEL[editUser.role] : ""}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wider">Nome</Label><Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-name" /></div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" value={editForm.email} disabled={editUser?.role !== "client"} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" data-testid="edit-user-email" />
              {editUser?.role !== "client" && <p className="text-[11px] text-muted-foreground mt-1">Il cambio email è consentito solo per gli utenti cliente.</p>}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditUser(null)} className="rounded-sm">Annulla</Button><Button onClick={saveEdit} className="rounded-sm" data-testid="save-edit-user-btn">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!pwdUser} onOpenChange={(o) => { if (!o) { setPwdUser(null); setPwdVal(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reset-pwd-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Key size={18} weight="bold" className="text-primary" /> Reset password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Imposta una nuova password per <b>{pwdUser?.email}</b>.</p>
            <div><Label className="text-xs uppercase tracking-wider">Nuova password</Label><Input type="text" value={pwdVal} onChange={(e) => setPwdVal(e.target.value)} placeholder="min. 8 caratteri" className="mt-1 rounded-sm" data-testid="reset-pwd-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setPwdUser(null); setPwdVal(""); }} className="rounded-sm">Annulla</Button><Button onClick={doResetPassword} className="rounded-sm" data-testid="reset-pwd-confirm">Reimposta</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
